
import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { checkAdminAuthorization } from '@/utils/authHelpers';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { AdminCompaniesTab } from '@/components/admin/tabs/AdminCompaniesTab';
import { AdminStatsTab } from '@/components/admin/tabs/AdminStatsTab';
import { AdminScrapesTabRefactored as AdminScrapesTab } from '@/components/admin/tabs/AdminScrapesTabRefactored';
import { AdminTasksTab } from '@/components/admin/tabs/AdminTasksTab';
import { AdminTagsTab } from '@/components/admin/tabs/AdminTagsTab';
import { AdminHeartbeatTab } from '@/components/admin/tabs/AdminHeartbeatTab';
import CompanyMetricsReview from '@/components/admin/metrics/CompanyMetricsReview';

const Admin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthorized, setIsAuthorized] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('scrapes');

  useEffect(() => {
    const checkAuth = async () => {
      await checkAdminAuthorization(navigate, (isAdmin) => {
        setIsAuthorized(isAdmin);
        setIsLoading(false);
      });
    };

    checkAuth();
  }, [navigate]);

  useEffect(() => {
    // Check if a default tab was specified in location state
    if (location.state && location.state.defaultTab) {
      const validTab = ['scrapes', 'stats', 'companies', 'metrics', 'tasks', 'tags', 'heartbeat'].includes(location.state.defaultTab)
        ? location.state.defaultTab
        : 'scrapes';
      setActiveTab(validTab);
    }
  }, [location.state]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50">
      <div className="container py-8">
        {isAuthorized ? (
          <div className="space-y-8">
            <div className="bg-white p-8 rounded-lg shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
              </div>

              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-7">
                  <TabsTrigger value="scrapes">Scrapes</TabsTrigger>
                  <TabsTrigger value="tasks">Tasks</TabsTrigger>
                  <TabsTrigger value="tags">Tags</TabsTrigger>
                  <TabsTrigger value="stats">Stats</TabsTrigger>
                  <TabsTrigger value="companies">Companies</TabsTrigger>
                  <TabsTrigger value="metrics">Metrics</TabsTrigger>
                  <TabsTrigger value="heartbeat">Heartbeat</TabsTrigger>
                </TabsList>

                <TabsContent value="scrapes">
                  <AdminScrapesTab />
                </TabsContent>
                <TabsContent value="tasks">
                  <AdminTasksTab />
                </TabsContent>
                <TabsContent value="tags">
                  <AdminTagsTab />
                </TabsContent>
                <TabsContent value="stats">
                  <AdminStatsTab />
                </TabsContent>
                <TabsContent value="companies">
                  <AdminCompaniesTab />
                </TabsContent>
                <TabsContent value="heartbeat">
                  <AdminHeartbeatTab />
                </TabsContent>
                <TabsContent value="metrics">
                  <div className="p-4">
                    <h1 className="text-2xl font-bold text-gray-900 mb-6">Metrics Review</h1>
                    <div className="flex items-center justify-between mb-4">
                      <p className="text-sm text-gray-500">
                        Loading data directly from database tables with pagination for complete results.
                      </p>
                    </div>
                    <CompanyMetricsReview onClose={() => {}} />
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </div>
        ) : (
          <div className="bg-white p-8 rounded-lg shadow-sm text-center">
            <h1 className="text-2xl font-bold text-red-600 mb-4">This is a restricted page</h1>
            <p className="text-gray-600">Unauthorized access.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
